import DOMPurify from 'dompurify';
import AppUtils from 'src/utils/AppUtils';

const IMAGE_REGEX_PATTERN = /<img[^>]+?src="(data:image\/[^;]+;base64[^"]+)"/gi;

const ERROR_IMAGE_URL = '/images/error-image.png';

const WHITESPACE_ENTITIES_REGEX_PATTERN = /&(nbsp|emsp|ensp|thinsp|#160|#32|#9|#8203);/gi;

/**
 * Text saved before a field became a rich-text editor is plain. TipTap output always opens with a block tag, so text
 * that merely starts with `<` (e.g. "<5 prompts") is still treated as plain.
 */
const EDITOR_HTML_PATTERN = /^\s*<(?:(?:p|ul|ol|h[1-6]|blockquote|table|hr)[\s>/]|pre><code[\s>])/i;

const HTML_ESCAPES = /** @type {const} */ ({
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
});

/**
 * @param {string} html
 * @param {DOMPurify.Config} [options] DOMPurify config
 * @returns {string}
 */
export function sanitizeHtml(html, options = {}) {
  return DOMPurify.sanitize(html, options);
}

/**
 * Escapes text for interpolation into an HTML string, such as one rendered with `v-html`. Anything
 * a user typed — a target or program name — must go through this, or it is rendered as markup.
 *
 * @param {*} text
 * @returns {string}
 */
export function escapeHtml(text) {
  return String(text ?? '').replace(/[&<>"']/g, (char) => HTML_ESCAPES[char]);
}

/**
 * An emptied editor still emits markup such as `<p></p>`,
 * so emptiness is judged on the visible text.
 *
 * @param {string | null | undefined} html
 * @returns {boolean}
 */
export function hasVisibleText(html) {
  if (typeof html !== 'string') {
    return false;
  }
  return html
    .replace(/<[^>]*>/g, '')
    .replace(WHITESPACE_ENTITIES_REGEX_PATTERN, ' ')
    .trim()
    .length > 0;
}

/**
 * @param {string | null | undefined} content
 * @returns {boolean} true for rich-text editor output, false for legacy plain text
 */
export function isEditorHtml(content) {
  return typeof content === 'string' && EDITOR_HTML_PATTERN.test(content);
}

/**
 * @param {string} html
 * @returns {string | null} the inner HTML when `html` is a single paragraph, otherwise null
 */
export function getSingleParagraphContent(html) {
  const trimmed = html.trim();
  const isParagraph = trimmed.startsWith('<p>') || trimmed.startsWith('<p ');
  if (!isParagraph || trimmed.indexOf('</p>') !== trimmed.length - '</p>'.length) {
    return null;
  }
  return trimmed.slice(trimmed.indexOf('>') + 1, -'</p>'.length);
}

/**
 * Moves each `{{token}}` out of the paragraph it sits in, so block HTML rendered in its place isn't nested in a `<p>`,
 * which browsers split into empty paragraphs (blank lines). Text on either side keeps a paragraph of its own.
 *
 * @param {string} html
 * @param {string} token template token name, e.g. `programMods`
 * @returns {string}
 */
export function moveTokenOutOfParagraph(html, token) {
  const placeholder = `{{${token}}}`;
  let output = '';
  let position = 0;
  let index = html.indexOf(placeholder);

  while (index !== -1) {
    const openStart = Math.max(html.lastIndexOf('<p>', index), html.lastIndexOf('<p ', index));
    const closeStart = html.indexOf('</p>', index);
    const isInParagraph = openStart >= position
      && closeStart !== -1
      && html.lastIndexOf('</p>', index) < openStart;

    if (isInParagraph) {
      const openEnd = html.indexOf('>', openStart) + 1;
      const openTag = html.slice(openStart, openEnd);
      // A formatted field (e.g. bold) leaves bare tags such as `<strong>` beside it, which would otherwise become
      // broken, empty paragraphs. They're dropped, as whitespace is.
      const parts = html.slice(openEnd, closeStart)
        .split(placeholder)
        .map((part) => (hasVisibleText(part) || part.includes('<img') ? `${openTag}${part}</p>` : ''));

      output += html.slice(position, openStart) + parts.join(placeholder);
      position = closeStart + '</p>'.length;
      index = html.indexOf(placeholder, position);
    } else {
      index = html.indexOf(placeholder, index + placeholder.length);
    }
  }

  return output + html.slice(position);
}

/**
 * Prepares notes (editor HTML or legacy plain text) for a `{{token}}` in a template. Single-paragraph notes stay
 * inline in the token's paragraph, as plain-text notes always did, joined by line breaks. Block notes (checklists,
 * lists, several paragraphs) can't sit inside a paragraph, so the token is moved out of it instead.
 *
 * @param {string} template HTML template containing `{{token}}`
 * @param {string} token
 * @param {Array<string | null | undefined>} notes
 * @returns {{ template: string, html: string }} the template to render, and the sanitized HTML for the token
 */
export function prepareNotesForToken(template, token, notes) {
  // Notes without text are skipped, as they'd otherwise render as empty lines.
  const notesHtml = notes
    .filter((note) => hasVisibleText(note))
    .map((note) => (isEditorHtml(note) ? sanitizeHtml(note) : `<p>${escapeHtml(note)}</p>`));
  const inlineNotes = notesHtml.map(getSingleParagraphContent);

  if (inlineNotes.every((note) => note !== null)) {
    return { template, html: inlineNotes.join('<br />') };
  }
  return { template: moveTokenOutOfParagraph(template, token), html: notesHtml.join('') };
}

/**
 * Extracts all unique base64-encoded image sources from an HTML string,
 * excluding images hosted on Firebase Storage.
 *
 * Moved from OfflineAccessUtils.js
 *
 * @param {string} str - The HTML string to search for base64 image sources.
 * @returns {string[]} An array of unique base64 image source strings.
 */
export function findBase64Images(str) {
  const matches = new Set();

  // Find all external images src
  let match = IMAGE_REGEX_PATTERN.exec(str);
  while (match) {
    // @todo Why exclude Firebase Storage here - they would never match the IMAGE_REGEX_PATTERN?
    if (match && match[1] && !match[1].includes('https://firebasestorage.googleapis.com/')) {
      matches.add(match[1]);
    }

    match = IMAGE_REGEX_PATTERN.exec(str);
  }

  return Array.from(matches);
}

/**
 * Replaces base64-encoded image sources in HTML content with corresponding URLs from a provided map.
 *
 * This function searches for image tags with base64-encoded `src` attributes, computes a hash for each base64 string,
 * and replaces the `src` attribute with a URL from the `urlMap` based on the hash. If no matching URL is found,
 * a default error image URL is used. Other attributes in the image tag are preserved.
 *
 * Remember that urlMap keys are hashes of base64 strings, not the base64 strings themselves.
 *
 * @async
 * @param {string} content - The HTML content containing image tags with base64-encoded sources.
 * @param {Object.<string, string>} urlMap - An object mapping image hashes to their corresponding URLs.
 * @returns {Promise<string>} The updated HTML content with base64 image sources replaced by URLs.
 */
export async function replaceBase64ImagesWithUrls(content, urlMap) {
  // Use async replacer for hashing
  const matches = [];
  let match = IMAGE_REGEX_PATTERN.exec(content);
  while (match !== null) {
    matches.push({
      fullTag: match[0],
      base64Src: match[1],
      index: match.index,
    });
    match = IMAGE_REGEX_PATTERN.exec(content);
  }

  // If no matches, return original content
  if (matches.length === 0) {
    return content;
  }

  // Compute hashes and replacements
  const replacementPromise = matches.map(async ({ fullTag, base64Src }) => {
    const imgHash = await AppUtils.hashString(base64Src);
    const url = urlMap[imgHash] || ERROR_IMAGE_URL;
    // Replace src attribute only, preserve other attributes
    const newTag = fullTag.replace(/src="[^"]+"/, `src="${url}"`);
    return { fullTag, newTag };
  });
  const replacements = await Promise.all(replacementPromise);

  // Replace all in one pass
  let updatedContent = content;
  replacements.forEach(({ fullTag, newTag }) => {
    updatedContent = updatedContent.replace(fullTag, newTag);
  });

  return updatedContent;
} // replaceBase64ImagesWithUrls()
